import { useMindMapStore } from '../store/useMindMapStore';
import { parseMarkdownToTree, convertTreeToMarkdown } from '../utils/markdownParser';
import { convertImageToMarkdown } from '../utils/importImage';
import { parseXMindToMarkdown } from '../utils/xmindParser';
import { INITIAL_MARKDOWN, PALETTES } from '../constants';
import { MindMapNode } from '../types';
import { RefObject } from 'react';

export const useFileHandlers = (mindMapRef: RefObject<any>, executeWithCheck: (action: () => void) => void) => {
  const store = useMindMapStore();

  const assignDefaultQuadrants = (root: MindMapNode) => {
      if (!root.children) return;
      const SIDE_CAPACITY = 10;
      root.children.forEach((child, index) => {
          if (!child.quadrant && !child.isFloating) {
              if (index < SIDE_CAPACITY) {
                  child.quadrant = index % 2 === 0 ? 'right' : 'left';
              } else {
                  const mod = (index - SIDE_CAPACITY) % 4;
                  if (mod === 0) child.quadrant = 'top';
                  else if (mod === 1) child.quadrant = 'bottom';
                  else if (mod === 2) child.quadrant = 'right';
                  else child.quadrant = 'left';
              }
          }
      });
  };

  const handleFileUpload = (content: string, name: string, force: boolean = false, initialHistory?: MindMapNode[], initialDirty?: boolean, mindmapId?: string | null) => {
    if (!force && store.isDirty) {
      executeWithCheck(() => handleFileUpload(content, name, true, initialHistory, initialDirty, mindmapId));
      return;
    }

    const targetMapId = mindmapId !== undefined ? mindmapId : null;
    store.setCurrentMindmapId(targetMapId);
    store.setSyncStatus(targetMapId ? 'saved' : 'local');

    store.setRawMarkdown(content);
    store.setFileName(name);
    
    const result = parseMarkdownToTree(content);
    
    if (result.root) {
        assignDefaultQuadrants(result.root);
    }

    store.setData(result.root);
    store.setHistory(initialHistory || result.config.history || []); 
    
    if (result.config.view) {
        store.setInitialView(result.config.view);
    } else {
        store.setInitialView(undefined);
    }
    
    if (result.config.themeName) {
        const foundPalette = PALETTES.find(p => p.name === result.config.themeName);
        if (foundPalette) {
            store.setPalette(foundPalette);
            store.setIsDarkMode(foundPalette.type === 'dark');
        }
    }
    
    store.setSelectedNode(null);
    store.setLastAddedNodeId(null);
    store.setMountKey(prev => prev + 1);
    store.setIsDirty(initialDirty ?? false); 
  };

  const handleCreateNew = () => {
      executeWithCheck(() => {
        const emptyMap = "# New Mind Map\n- Main Topic 1\n- Main Topic 2\n- Main Topic 3\n- Main Topic 4";
        handleFileUpload(emptyMap, "Untitled.md", true); 
        store.setCurrentMindmapId(null);
        store.setSyncStatus('local');
      });
  };

  const handleImageUpload = async (file: File) => {
    store.setIsImportingImage(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const base64String = (event.target?.result as string).split(',')[1];
          const mimeType = file.type;
          const markdown = await convertImageToMarkdown(base64String, mimeType);
          const name = file.name.replace(/\.[^/.]+$/, "") + ".md";
          
          handleFileUpload(markdown, name, true);
        } catch (error: any) {
          console.error("Failed to import image:", error);
          alert(`Failed to import image: ${error.message || "Unknown error"}`);
        } finally {
          store.setIsImportingImage(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error(error);
      store.setIsImportingImage(false);
    }
  };

  const handleXMindUpload = async (file: File) => {
    try {
      const markdown = await parseXMindToMarkdown(file);
      const name = file.name.replace(/\.[^/.]+$/, "") + ".md";
      handleFileUpload(markdown, name, true);
    } catch (error: any) {
      console.error("Failed to import XMind:", error);
      alert(`Failed to import XMind: ${error.message || "Unknown error"}`);
    }
  };

  const handleReset = () => {
      executeWithCheck(() => {
        const result = parseMarkdownToTree(store.rawMarkdown);
        if (result.root) {
          assignDefaultQuadrants(result.root);
        }
  
        store.setData(result.root);
        store.setHistory([]);
        
        if (result.config.view) {
            store.setInitialView(result.config.view);
        } else {
            store.setInitialView(undefined);
        }

        if (result.config.themeName) {
            const foundPalette = PALETTES.find(p => p.name === result.config.themeName);
            if (foundPalette) {
                store.setPalette(foundPalette);
                store.setIsDarkMode(foundPalette.type === 'dark');
            }
        }
        
        store.setRearrangeTrigger(0);
        store.setLastAddedNodeId(null);
        store.setMountKey(prev => prev + 1);
        store.setIsDirty(false); 
      });
  };

  const handleSave = () => {
      if (!store.data) return;
      
      let saveName = store.fileName;
      if (store.fileName === "Untitled.md" || store.fileName === "Mindmap demo.md") {
          const coreName = store.data.name.trim();
          const sanitized = coreName.replace(/[^a-z0-9 \u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf_\-\.]/gi, "_").substring(0, 50);
          if (sanitized) {
              saveName = `${sanitized}.md`;
              store.setFileName(saveName); 
          }
      }

      const viewState = mindMapRef.current?.getViewState();
      const markdown = convertTreeToMarkdown(store.data, store.palette.name, viewState, store.history);
      const blob = new Blob([markdown], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = saveName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      store.setIsDirty(false); 
  };

  const handleExportImage = () => {
    const name = store.fileName.replace(/\.(md|txt)$/i, '');
    if (mindMapRef.current) {
        mindMapRef.current.exportImage(name);
    }
  };

  return {
    handleFileUpload,
    handleCreateNew,
    handleImageUpload,
    handleXMindUpload,
    handleReset,
    handleSave,
    handleExportImage
  };
};
